import { Table } from '@montugroup/design-system';
import { Box, useTheme } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import type { CSSProperties } from 'react';
import { useExpandedRows } from './hooks/useExpandedRows';
import { useMedicationsTableData } from './hooks/useMedicationsTableData';
import type {
  PrescriptionMedicationsTableProps,
  PrescriptionMedicationsTableRow,
} from './PrescriptionMedicationsTable.types';
import {
  applyRowStyles,
  formatActionCell,
  formatCenteredCell,
  formatProductNameCell,
  formatStatusCell,
} from './PrescriptionMedicationsTable.utils';

const columnHelper = createColumnHelper<PrescriptionMedicationsTableRow>();

export const PrescriptionMedicationsTable = ({
  prescribedProducts,
  isLoading,
  onCancelProduct,
  isProductCancellable,
  hasCancelPermissions,
  prescriptionStatus,
  openRefillPrescriptionUrl,
}: PrescriptionMedicationsTableProps) => {
  const theme = useTheme();
  const { expandedRows, onRowToggled } = useExpandedRows();

  const productsData = useMedicationsTableData({ prescribedProducts, prescriptionStatus, expandedRows });

  const centeredColumnStyle: CSSProperties = {
    width: theme.spacing(22),
    textAlign: 'center',
  };

  const displayColumns = [
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (item) => formatStatusCell(item),
      meta: {
        style: {
          width: theme.spacing(28),
        },
      },
    }),
    columnHelper.accessor('name', {
      header: 'Product',
      enableSorting: false,
      cell: (item) => formatProductNameCell(item, onRowToggled),
    }),
    columnHelper.accessor('createdDate', {
      header: 'Date',
      enableSorting: false,
      cell: (item) => DateTime.fromISO(item.getValue()).toFormat('dd MMM yyyy'),
      meta: {
        style: {
          width: theme.spacing(32),
        },
      },
    }),
    columnHelper.accessor('quantity', {
      header: 'Qty',
      enableSorting: false,
      cell: (item) => formatCenteredCell(item.getValue()?.toString() || '-'),
      meta: {
        style: centeredColumnStyle,
      },
    }),
    columnHelper.accessor('repeats', {
      header: 'Repeats',
      enableSorting: false,
      cell: (item) => formatCenteredCell(item.getValue()?.toString() || '0'),
      meta: {
        style: centeredColumnStyle,
      },
    }),
    columnHelper.accessor('interval', {
      header: 'Interval',
      enableSorting: false,
      cell: (item) => formatCenteredCell(item.getValue()?.toString() || '-'),
      meta: {
        style: centeredColumnStyle,
      },
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (item) =>
        formatActionCell(item, onCancelProduct, isProductCancellable, openRefillPrescriptionUrl, hasCancelPermissions),
      meta: {
        style: centeredColumnStyle,
      },
    }),
  ];

  return (
    <Box
      sx={{
        '& .MuiTableContainer-root': {
          border: 'none',
        },
      }}
    >
      <Table
        data={productsData}
        columns={displayColumns}
        isLoading={isLoading}
        pageSize={100}
        elevation={0}
        hasRowBackgroundColor={false}
        applyRowStyles={applyRowStyles}
      />
    </Box>
  );
};
